import { useQueries, useQuery } from "@tanstack/react-query";
import {
  ArrowDown, ArrowUp, ChevronsDown, ChevronsUp, Download, Eye, EyeOff, Info, Search,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Area, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { MultiSelect } from "@/components/MultiSelect";
import {
  getComprasMdaRangoFechas,
  getComprasMdaSerie,
  type ComprasMdaSerie,
  type ComprasMdaSerieAgrupacion,
  type ComprasMdaSistema,
} from "@/lib/api-mercado";

/*
 * Línea de tiempo de las compras en el MDA: una serie por contraparte entre dos
 * fechas, más el pastel del acumulado del periodo.
 *
 * Es la misma demanda fija ofertada que resume /compras-mda (participante ×
 * zona agrupada), leída en el otro eje: aquí no se cruzan precios, solo MW.
 *
 * El grueso del mercado es un solo comprador (CFE Suministrador de Servicios
 * Básicos, ~85 % del total), así que el pastel casi siempre sale dominado por
 * una rebanada: para comparar al resto entre sí conviene deseleccionarlo en el
 * filtro de contrapartes.
 */

const nf0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 1, maximumFractionDigits: 1,
});

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/* Periodo: "YYYY-MM" (mes), "YYYY-MM-DD" (día) o "YYYY-MM-DD HH" (hora). */
function partes(p: string): { y: string; m: string; d?: string; h?: string } {
  const [fecha, h] = p.split(" ");
  const [y, m, d] = fecha.split("-");
  return { y, m, d, h };
}
function fmtPeriodo(p: string): string {
  const { y, m, d, h } = partes(p);
  if (!d) return `${MESES[Number(m) - 1]} ${y}`;
  return h ? `${d} ${MESES[Number(m) - 1]} ${h}h` : `${d} ${MESES[Number(m) - 1]}`;
}
function fmtPeriodoLargo(p: string): string {
  const { y, m, d, h } = partes(p);
  if (!d) return `${MESES[Number(m) - 1]} ${y}`;
  const fecha = `${d} ${MESES[Number(m) - 1]} ${y}`;
  return h ? `${fecha}, hora ${Number(h)}` : fecha;
}

/** Fecha ISO desplazada `dias` (negativo = hacia atrás). */
function masDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}
const diasEntre = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 86_400_000);

// Cuánto rango admite cada resolución en la UI. La horaria ya no está topada
// a un mes como el endpoint: la página la pide MES POR MES (ver abajo).
const TOPE_DIAS: Record<string, number> = { hora: 400, dia: 400, mes: Infinity };

// Tope del rango completo (el backend responde 422 más allá): el barrido crece
// rápido y Cloud Run corta a los 60 s. Medido: 34 s en 36 meses, 58 s en 60.
const TOPE_RANGO_DIAS = 1100;

/** Cuántos trozos horarios se piden a la vez. */
const EN_VUELO = 4;

/** La misma fecha n meses atrás (recorta el día si el mes destino es más corto). */
function masMeses(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const total = y * 12 + (m - 1) + n;
  const ay = Math.floor(total / 12);
  const am = (total % 12) + 1;
  const ultimo = new Date(Date.UTC(ay, am, 0)).getUTCDate();
  return `${ay}-${String(am).padStart(2, "0")}-${String(Math.min(d, ultimo)).padStart(2, "0")}`;
}

/** Parte un rango en tramos de un AÑO de calendario, recortados a sus extremos. */
function aniosEntre(desde: string, hasta: string): { desde: string; hasta: string }[] {
  const out: { desde: string; hasta: string }[] = [];
  for (let y = Number(desde.slice(0, 4)); y <= Number(hasta.slice(0, 4)); y++) {
    const ini = `${y}-01-01`;
    const fin = `${y}-12-31`;
    out.push({ desde: ini < desde ? desde : ini, hasta: fin > hasta ? hasta : fin });
  }
  return out;
}

/** Parte un rango en tramos de un mes de calendario, recortados a sus extremos. */
function mesesEntre(desde: string, hasta: string): { desde: string; hasta: string }[] {
  const out: { desde: string; hasta: string }[] = [];
  const [y0, m0] = desde.split("-").map(Number);
  const [y1, m1] = hasta.split("-").map(Number);
  let y = y0;
  let m = m0;
  while (y * 12 + m <= y1 * 12 + m1) {
    const ini = `${y}-${String(m).padStart(2, "0")}-01`;
    const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const fin = `${y}-${String(m).padStart(2, "0")}-${String(ultimo).padStart(2, "0")}`;
    out.push({ desde: ini < desde ? desde : ini, hasta: fin > hasta ? hasta : fin });
    if (m === 12) { y += 1; m = 1; } else { m += 1; }
  }
  return out;
}

/*
 * Paleta categórica de 20 tonos (tres bandas de luminosidad × tonos repartidos
 * por ángulo áureo), ordenada para maximizar la separación del par vecino PEOR:
 * ΔE 7.2 con daltonismo protán. Con 20 series el color por sí solo YA NO basta
 * para distinguirlas —ningún juego de 20 lo lograría—, por eso la identidad se
 * apoya en la leyenda (que además apaga series), el tooltip y la tabla.
 *
 * Se asignan en orden fijo por el lugar que ocupa la contraparte en el
 * acumulado del periodo, NO por cuáles estén dibujadas: así apagar o filtrar
 * series no repinta a las que quedan. De la 26ª en adelante, gris.
 */
const PALETA = [
  "#8b7d00", "#31a7f3", "#925200", "#4074dd", "#c45400",
  "#4e59b1", "#bf9a00", "#5a55af", "#eb7a52", "#8c418b",
  "#6eb452", "#517000", "#00bbb3", "#a43a41", "#a689f1",
  "#bf5a00", "#0092b1", "#c84368", "#9f55be", "#5bb661",
];
const GRIS = "#94a3b8";

/** Clave de la serie agregada con todo lo que no entró al top. */
const OTROS = "(otros)";

/** Recorta la etiqueta sin comerse la clave: "Razón social… [C001]". */
function recorta(label: string, max: number): string {
  if (label.length <= max) return label;
  const i = label.lastIndexOf(" [");
  if (i < 0) return `${label.slice(0, max)}…`;
  const clave = label.slice(i);
  return `${label.slice(0, Math.max(3, max - clave.length - 1))}…${clave}`;
}

function descargarCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ComprasMdaSeriePage() {
  // "" = que el backend decida: el año corrido hasta la última fecha publicada.
  /* Los filtros que MUEVEN datos viven en dos copias: `form` es lo que el
   * usuario está capturando y `aplicado` lo que se está consultando. Nada pesado
   * sale hasta que se le da a Buscar — un rango largo son minutos de barrido y
   * no tiene por qué dispararse al teclear una fecha. */
  type Filtros = {
    desde: string;
    hasta: string;
    agrupacion: ComprasMdaSerieAgrupacion;
    sistema: ComprasMdaSistema; // "" = SIN + BCA + BCS sumados
    seleccion: string[]; // [] = las mayores
  };
  const INICIAL: Filtros = {
    desde: "", hasta: "", agrupacion: "hora", sistema: "", seleccion: [],
  };
  const [form, setForm] = useState<Filtros>(INICIAL);
  const [aplicado, setAplicado] = useState<Filtros>(INICIAL);
  const { desde, hasta, agrupacion, sistema, seleccion } = aplicado;
  /* Nada se consulta al entrar: la página abre vacía con el rango por default
   * ya escrito en los inputs y espera el clic en Buscar. Un barrido largo son
   * minutos, no tiene por qué dispararse solo por abrir la pestaña. */
  const [buscado, setBuscado] = useState(false);
  const buscar = (f: Filtros) => {
    setAplicado(f);
    setBuscado(true);
  };
  const pendiente = !buscado || JSON.stringify(form) !== JSON.stringify(aplicado);
  // Series apagadas desde la leyenda (solo afectan lo dibujado, no la consulta).
  const [apagadas, setApagadas] = useState<Set<string>>(new Set());
  const [tipo, setTipo] = useState<"lineas" | "apiladas">("lineas");
  // Orden del apilado, de la base hacia arriba. null = el del acumulado.
  const [orden, setOrden] = useState<string[] | null>(null);

  // Ventanas deslizantes de las dos fases: las lee el `enabled` de cada tramo.
  const resueltosA = useRef(0);
  const resueltosB = useRef(0);

  /* El rango se resuelve AQUÍ, con una consulta de centavos (min/max de lo
   * publicado). Antes lo resolvía la propia petición de datos, que en un rango
   * largo ni siquiera cabe en el tiempo que aguanta Cloud Run. */
  const limites = useQuery({
    queryKey: ["compras-mda-serie", "rango"],
    queryFn: getComprasMdaRangoFechas,
    staleTime: 30 * 60_000,
  });

  const rango = useMemo(() => {
    const lim = limites.data;
    if (!lim) return null;
    const fin = hasta && hasta <= lim.fecha_max ? hasta : lim.fecha_max;
    // Default: UN MES MÓVIL contado desde la última fecha publicada (no desde
    // hoy: lo del MDA llega con rezago y arrancar en hoy deja la gráfica corta).
    const ini0 = desde || masMeses(lim.fecha_max, -1);
    const ini = ini0 < lim.fecha_min ? lim.fecha_min : ini0 > fin ? fin : ini0;
    return { desde: ini, hasta: fin, fecha_min: lim.fecha_min, fecha_max: lim.fecha_max };
  }, [limites.data, desde, hasta]);

  const largo = rango ? diasEntre(rango.desde, rango.hasta) : 0;

  /* Tramos: UNA PETICIÓN POR AÑO (por mes en resolución horaria). Ninguno se
   * acerca al tope por petición del backend, así que un rango de 2017 a la
   * fecha ya no truena: se arma por partes. */
  const tramos = useMemo(() => {
    if (!rango) return [];
    return agrupacion === "hora"
      ? mesesEntre(rango.desde, rango.hasta)
      : aniosEntre(rango.desde, rango.hasta);
  }, [rango, agrupacion]);

  /* FASE A — cuánto compró cada contraparte en el periodo. Va por tramos y
   * agrupada por mes (lo más barato); de aquí salen el pastel, la tabla, los
   * colores y QUÉ contrapartes se dibujan. `top: 1` porque las series de esta
   * fase no se usan, solo el acumulado. */
  const totalesQ = useQueries({
    queries: tramos.map((t, i) => ({
      queryKey: ["compras-mda-serie", "totales", t.desde, t.hasta, sistema],
      queryFn: () =>
        getComprasMdaSerie({
          desde: t.desde,
          hasta: t.hasta,
          agrupacion: "mes" as const,
          sistema,
          top: 1,
        }),
      staleTime: 30 * 60_000,
      enabled: buscado && i < resueltosA.current + EN_VUELO,
    })),
  });
  resueltosA.current = totalesQ.filter((q) => q.data || q.error).length;
  const totalesListos = tramos.length > 0 && totalesQ.every((q) => q.data);

  /** Acumulado del periodo completo, sumando los tramos. */
  const totales = useMemo(() => {
    const suma = new Map<string, { nombre: string; total: number }>();
    for (const q of totalesQ) {
      for (const t of q.data?.totales ?? []) {
        const acc = suma.get(t.participante);
        if (acc) acc.total += t.total;
        else suma.set(t.participante, { nombre: t.nombre, total: t.total });
      }
    }
    return [...suma]
      .map(([participante, v]) => ({
        participante,
        nombre: v.nombre,
        total: Math.round(v.total * 100) / 100,
      }))
      .sort((a, b) => b.total - a.total);
  }, [totalesQ]);

  /* Cuántas series se dibujan: 20 de normal y 10 en rangos largos (más de 36
   * meses), donde cada serie ya trae muchísimo punto. */
  const limite = largo > TOPE_RANGO_DIAS ? 10 : 20;
  const claves = useMemo(
    () =>
      seleccion.length
        ? seleccion
        : totales.slice(0, limite).map((t) => t.participante),
    [seleccion, totales, limite],
  );

  /* FASE B — el detalle en la resolución elegida, tramo por tramo, pidiendo
   * exactamente esas contrapartes para que las series no cambien entre tramos.
   * Arranca cuando la fase A terminó: si no, el top cambiaría a media carga. */
  const detalleQ = useQueries({
    queries: (buscado && totalesListos ? tramos : []).map((t, i) => ({
      queryKey: ["compras-mda-serie", "detalle", agrupacion, sistema, t.desde, t.hasta, claves],
      queryFn: () =>
        getComprasMdaSerie({
          desde: t.desde,
          hasta: t.hasta,
          agrupacion,
          sistema,
          participantes: claves,
          // "Otros" solo cuando NO se filtró a mano: si el usuario eligió
          // contrapartes, un "Otros" con todas las demás no diría nada.
          otros: seleccion.length === 0,
        }),
      staleTime: 5 * 60_000,
      enabled: i < resueltosB.current + EN_VUELO,
    })),
  });
  resueltosB.current = detalleQ.filter((q) => q.data || q.error).length;
  const listos = detalleQ.filter((q) => q.data).length;

  /** Vista unificada: los tramos ya pegados, con la forma del endpoint. */
  const data: ComprasMdaSerie | undefined = useMemo(() => {
    if (!rango || claves.length === 0) return undefined;
    const periodos: string[] = [];
    const valores = new Map<string, number[]>(claves.map((c) => [c, []]));
    const valoresOtros: number[] = [];
    const nombres = new Map<string, string>();
    let conOtros = false;
    for (const q of detalleQ) {
      const d = q.data;
      if (!d) break; // solo se pega el PREFIJO cargado: nunca queda un hueco
      periodos.push(...d.periodos);
      const ceros = () => Array(d.periodos.length).fill(0) as number[];
      for (const c of claves) {
        const s = d.series.find((x) => x.participante === c);
        if (s?.nombre) nombres.set(c, s.nombre);
        valores.get(c)!.push(...(s ? s.valores : ceros()));
      }
      valoresOtros.push(...(d.otros ? d.otros.valores : ceros()));
      if (d.otros) conOtros = true;
    }
    if (periodos.length === 0) return undefined;

    const series = claves.map((c) => ({
      participante: c,
      nombre: nombres.get(c) ?? totales.find((t) => t.participante === c)?.nombre ?? "",
      total: totales.find((t) => t.participante === c)?.total ?? 0,
      valores: valores.get(c) ?? [],
    }));
    const total = Math.round(totales.reduce((a, t) => a + t.total, 0) * 100) / 100;
    const otros = conOtros
      ? {
          contrapartes: Math.max(totales.length - claves.length, 0),
          total: Math.round((total - series.reduce((a, s) => a + s.total, 0)) * 100) / 100,
          valores: valoresOtros,
        }
      : null;
    return {
      desde: rango.desde,
      hasta: rango.hasta,
      fecha_min: rango.fecha_min,
      fecha_max: rango.fecha_max,
      agrupacion,
      periodos,
      series: otros
        ? [
            ...series,
            {
              participante: OTROS,
              nombre: `Otros (${otros.contrapartes} contrapartes)`,
              total: otros.total,
              valores: otros.valores,
            },
          ]
        : series,
      otros,
      totales,
      total,
    };
  }, [detalleQ, claves, totales, rango, agrupacion]);

  const error = (limites.error ??
    totalesQ.find((q) => q.error)?.error ??
    detalleQ.find((q) => q.error)?.error) as Error | undefined;
  const cargando =
    buscado && (limites.isFetching || !totalesListos || listos < tramos.length);

  const visibles = useMemo(
    () => (data?.series ?? []).filter((s) => !apagadas.has(s.participante)),
    [data?.series, apagadas],
  );

  /* Orden del apilado: el capturado por el usuario, reconciliado contra las
   * series que hay ahora (se caen las que ya no están, se agregan las nuevas al
   * final). Sin orden propio, manda el acumulado del periodo. */
  const ordenStack = useMemo(() => {
    const actuales = (data?.series ?? []).map((s) => s.participante);
    if (!orden) return actuales;
    const vivas = orden.filter((p) => actuales.includes(p));
    return [...vivas, ...actuales.filter((p) => !vivas.includes(p))];
  }, [orden, data?.series]);

  const mover = (p: string, delta: number) => {
    const i = ordenStack.indexOf(p);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ordenStack.length) return;
    const next = [...ordenStack];
    [next[i], next[j]] = [next[j], next[i]];
    setOrden(next);
  };

  /** Manda una serie al inicio (la base) o al final (arriba de todo). */
  const alExtremo = (p: string, donde: "inicio" | "final") => {
    const resto = ordenStack.filter((x) => x !== p);
    setOrden(donde === "inicio" ? [p, ...resto] : [...resto, p]);
  };

  /** Series en orden de apilado, ya sin las apagadas (la base va primero). */
  const apiladas = useMemo(() => {
    const porClave = new Map((data?.series ?? []).map((s) => [s.participante, s]));
    return ordenStack
      .filter((p) => !apagadas.has(p))
      .map((p) => porClave.get(p))
      .filter((s): s is NonNullable<typeof s> => !!s);
  }, [ordenStack, apagadas, data?.series]);

  const alternar = (p: string) =>
    setApagadas((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  /* Escala del eje Y: se recalcula con lo VISIBLE. Recharts no siempre excluye
   * del dominio a las series ocultas, así que el máximo se calcula aquí. */
  const maxY = useMemo(() => {
    let m = 0;
    if (tipo === "apiladas") {
      const n = data?.periodos.length ?? 0;
      for (let i = 0; i < n; i++) {
        let suma = 0;
        for (const s of visibles) suma += s.valores[i] ?? 0;
        if (suma > m) m = suma;
      }
    } else {
      for (const s of visibles) for (const v of s.valores) if (v > m) m = v;
    }
    return m > 0 ? Math.ceil(m * 1.05) : 0;
  }, [visibles, tipo, data?.periodos.length]);

  // Al primer resultado se fijan las fechas en los inputs, para que el usuario
  // vea qué rango está mirando aunque los haya dejado vacíos.
  useEffect(() => {
    if (rango && !form.desde && !form.hasta) {
      const fechas = { desde: rango.desde, hasta: rango.hasta };
      setForm((f) => ({ ...f, ...fechas }));
      setAplicado((a) => ({ ...a, ...fechas }));
    }
  }, [rango, form.desde, form.hasta]);

  // Color por contraparte: por su lugar en el acumulado del periodo.
  const colorPor = useMemo(() => {
    const m = new Map<string, string>();
    (data?.totales ?? []).forEach((t, i) => {
      m.set(t.participante, i < PALETA.length ? PALETA[i] : GRIS);
    });
    m.set(OTROS, GRIS);
    return m;
  }, [data?.totales]);

  // Etiqueta visible de una contraparte: "Descripción [CLAVE]". El código del
  // participante va al final y entre corchetes para que la razón social sea lo
  // que se lee primero (y lo que ordena alfabéticamente el filtro).
  const etiqueta = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of data?.totales ?? []) {
      m.set(t.participante, t.nombre ? `${t.nombre} [${t.participante}]` : `[${t.participante}]`);
    }
    if (data?.otros) m.set(OTROS, `Otros (${data.otros.contrapartes} contrapartes)`);
    return m;
  }, [data?.totales, data?.otros]);

  // De la etiqueta de vuelta a la clave: el MultiSelect trabaja con texto, pero
  // al backend siempre va la clave.
  const claveDe = useMemo(() => {
    const m = new Map<string, string>();
    for (const [clave, label] of etiqueta) m.set(label, clave);
    return m;
  }, [etiqueta]);

  // Filas de la gráfica de líneas: una por periodo, una columna por serie.
  const filas = useMemo(() => {
    if (!data) return [];
    return data.periodos.map((p, i) => {
      const fila: Record<string, string | number> = { periodo: p };
      for (const s of data.series) fila[s.participante] = s.valores[i];
      return fila;
    });
  }, [data]);

  // Pastel: las 20 mayores del periodo + "Otros" agregados (una rebanada por
  // contraparte con 88 de ellas sería ilegible). Respeta las series apagadas en
  // la leyenda de la línea de tiempo, para poder ver la composición sin el
  // comprador que se come el pastel.
  const pastel = useMemo(() => {
    if (!data) return [];
    const vivas = data.totales.filter((t) => !apagadas.has(t.participante));
    const top = vivas.slice(0, PALETA.length);
    const resto = vivas.slice(PALETA.length);
    const otros = resto.reduce((a, t) => a + t.total, 0);
    const out = top.map((t) => ({
      key: t.participante,
      nombre: etiqueta.get(t.participante) ?? t.participante,
      total: t.total,
      color: colorPor.get(t.participante) ?? GRIS,
    }));
    if (otros > 0) {
      out.push({
        key: "(otros)",
        nombre: `Otros (${resto.length} contrapartes)`,
        total: Math.round(otros * 100) / 100,
        color: GRIS,
      });
    }
    return out;
  }, [data, colorPor, etiqueta, apagadas]);

  /** Base del porcentaje del pastel: lo que se está mostrando. */
  const totalPastel = useMemo(
    () => pastel.reduce((a, r) => a + r.total, 0),
    [pastel],
  );

  const opciones = useMemo(
    () => (data?.totales ?? []).map((t) => etiqueta.get(t.participante) ?? t.participante),
    [data?.totales, etiqueta],
  );

  /* Cambiar de resolución con un rango que no le cabe (p. ej. el año corrido
   * en vista horaria) daría 422: se recorta al tope de la nueva, terminando en
   * `hasta`. */
  const cambiarAgrupacion = (a: ComprasMdaSerieAgrupacion) => {
    setForm((f) => {
      const fin = f.hasta || rango?.hasta;
      const ini = f.desde || rango?.desde;
      if (fin && ini && diasEntre(ini, fin) > TOPE_DIAS[a]) {
        return { ...f, agrupacion: a, desde: masDias(fin, -(TOPE_DIAS[a] - 1)), hasta: fin };
      }
      return { ...f, agrupacion: a };
    });
  };

  /** % sobre el total del periodo (tabla) y sobre lo mostrado (pastel). */
  const pctDe = (v: number) => (data && data.total > 0 ? (v / data.total) * 100 : 0);
  const pctPastel = (v: number) => (totalPastel > 0 ? (v / totalPastel) * 100 : 0);

  const csv = () => {
    if (!data) return;
    const cab = ["periodo", ...data.series.map((s) => s.participante)];
    const lineas = [cab.join(",")];
    data.periodos.forEach((p, i) => {
      lineas.push([p, ...data.series.map((s) => s.valores[i] ?? 0)].join(","));
    });
    descargarCsv(
      `compras_mda_serie_${sistema || "todos"}_${data.desde}_${data.hasta}.csv`,
      lineas.join("\n"),
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          Compras MDA — línea de tiempo por contraparte
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Demanda fija ofertada en el Mercado del Día en Adelanto, en MW, por
          contraparte y periodo. Misma fuente que{" "}
          <span className="font-medium">Compras MDA</span>; CENACE la publica con
          unos dos meses de rezago.
        </p>
      </div>

      {/* Filtros: una sola fila arriba de las gráficas */}
      <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Desde</span>
          <input
            type="date"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={form.desde}
            min={rango?.fecha_min}
            max={rango?.fecha_max}
            onChange={(e) => setForm({ ...form, desde: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Hasta</span>
          <input
            type="date"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={form.hasta}
            min={rango?.fecha_min}
            max={rango?.fecha_max}
            onChange={(e) => setForm({ ...form, hasta: e.target.value })}
          />
        </label>
        <div>
          <span className="text-xs font-medium text-slate-600">Sistema</span>
          <div className="mt-1 flex rounded border overflow-hidden">
            {([["", "Todos"], ["SIN", "SIN"], ["BCA", "BCA"], ["BCS", "BCS"]] as const).map(
              ([k, l]) => (
                <button
                  key={k || "todos"}
                  type="button"
                  onClick={() => setForm({ ...form, sistema: k, seleccion: [] })}
                  className={`px-3 py-1.5 text-sm ${
                    form.sistema === k
                      ? "bg-slate-800 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  title={
                    k === ""
                      ? "SIN + BCA + BCS sumados"
                      : `Solo el sistema ${k}`
                  }
                >
                  {l}
                </button>
              ),
            )}
          </div>
        </div>
        <div>
          <span className="text-xs font-medium text-slate-600">Agrupar por</span>
          <div className="mt-1 flex rounded border overflow-hidden">
            {(["hora", "dia", "mes"] as const).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => cambiarAgrupacion(a)}
                className={`px-3 py-1.5 text-sm ${
                  form.agrupacion === a
                    ? "bg-slate-800 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {a === "hora" ? "Hora" : a === "dia" ? "Día" : "Mes"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="text-xs font-medium text-slate-600">Gráfica</span>
          <div className="mt-1 flex rounded border overflow-hidden">
            {([["lineas", "Líneas"], ["apiladas", "Apiladas"]] as const).map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTipo(k)}
                className={`px-3 py-1.5 text-sm ${
                  tipo === k
                    ? "bg-slate-800 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <MultiSelect
          label="Contrapartes"
          options={opciones}
          selected={form.seleccion.map((p) => etiqueta.get(p) ?? p)}
          onChange={(labels) =>
            setForm({ ...form, seleccion: labels.map((l) => claveDe.get(l) ?? l) })
          }
          placeholder="Las 20 mayores"
          className="min-w-64"
        />
        {form.seleccion.length > 0 && (
          <button
            type="button"
            onClick={() => setForm({ ...form, seleccion: [] })}
            className="text-sm text-slate-500 hover:text-slate-800 underline pb-1.5"
          >
            Quitar filtro
          </button>
        )}
        <button
          type="button"
          onClick={() => buscar(form)}
          disabled={!pendiente || cargando}
          className={`inline-flex items-center gap-1.5 rounded px-4 py-1.5 text-sm ${
            pendiente && !cargando
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "border text-slate-400"
          }`}
          title="Los filtros no consultan hasta que le des clic: un rango largo son minutos de barrido"
        >
          <Search className="w-4 h-4" /> {cargando ? "Buscando…" : "Buscar"}
        </button>
        <div className="ml-auto flex items-end gap-3">
          {data && (
            <div className="text-right">
              <div className="text-xs text-slate-500">Total del periodo</div>
              <div className="text-lg font-semibold text-slate-900 tabular-nums">
                {nf0.format(data.total)} <span className="text-sm font-normal">MW</span>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={csv}
            disabled={!data}
            className="inline-flex items-center gap-1.5 text-xs border rounded px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      {!buscado && !cargando && (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-slate-500">
          Ajusta el rango y las contrapartes y dale <b>Buscar</b>. La página no
          consulta nada al abrirse: un periodo largo se trae por tramos y puede
          tardar varios minutos.
        </div>
      )}
      {buscado && pendiente && !cargando && (
        <div className="text-sm text-amber-700">
          Hay filtros sin aplicar: dale <b>Buscar</b>.
          {rango && diasEntre(form.desde || rango.desde, form.hasta || rango.hasta) > TOPE_RANGO_DIAS && (
            <> Ojo: ese rango pasa de {Math.round(TOPE_RANGO_DIAS / 30)} meses, se
            trae por tramos y puede tardar varios minutos la primera vez (después
            queda en caché). Se dibujarán las 10 mayores más “Otros”.</>
          )}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error.message}
        </div>
      )}
      {cargando && (
        <div className="text-sm text-slate-500">
          {!totalesListos
            ? `Midiendo el periodo: tramo ${Math.min(resueltosA.current + 1, tramos.length)} de ${tramos.length}…`
            : `Trayendo el detalle: tramo ${Math.min(listos + 1, tramos.length)} de ${tramos.length}… la gráfica se dibuja conforme llegan.`}
        </div>
      )}

      {data && data.periodos.length > 0 && (
        <>
          {/* Línea de tiempo */}
          <div className="bg-white border rounded-lg p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-1">
              Compras por {agrupacion === "hora" ? "hora" : agrupacion === "dia" ? "día" : "mes"} (MW)
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              {fmtPeriodoLargo(data.desde)} — {fmtPeriodoLargo(data.hasta)} ·{" "}
              {sistema ? `solo ${sistema}` : "SIN + BCA + BCS"} ·{" "}
              {visibles.length} de {data.series.length} series dibujadas (
              {data.totales.length} contrapartes con compras en el periodo) ·{" "}
              {tipo === "apiladas"
                ? "el panel de abajo manda el orden del apilado y qué se ve"
                : "clic en la leyenda para apagar o encender una"}
              {apagadas.size > 0 && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setApagadas(new Set())}
                    className="underline hover:text-slate-800"
                  >
                    encender todas
                  </button>
                </>
              )}
            </p>
            {tipo === "apiladas" && (
              <div className="border rounded mb-3">
                <div className="px-3 py-1.5 bg-slate-50 border-b flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600">
                    Orden del apilado — de la base hacia arriba
                  </span>
                  <button
                    type="button"
                    onClick={() => setOrden(null)}
                    disabled={orden === null}
                    className="text-xs text-slate-500 hover:text-slate-800 underline disabled:no-underline disabled:opacity-40"
                  >
                    Ordenar por acumulado
                  </button>
                </div>
                <ul className="max-h-44 overflow-y-auto divide-y">
                  {ordenStack.map((clave, i) => {
                    const off = apagadas.has(clave);
                    return (
                      <li key={clave} className="flex items-center gap-2 px-3 py-1 text-xs">
                        <span className="w-5 text-slate-400 tabular-nums">{i + 1}</span>
                        <span
                          className="w-2.5 h-2.5 rounded-sm shrink-0"
                          style={{ background: colorPor.get(clave) ?? GRIS, opacity: off ? 0.3 : 1 }}
                        />
                        <span className={off ? "text-slate-400 line-through" : "text-slate-700"}>
                          {etiqueta.get(clave) ?? clave}
                        </span>
                        <span className="ml-auto flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => alExtremo(clave, "inicio")}
                            disabled={i === 0}
                            title="Subir al inicio (a la base del apilado)"
                            className="p-0.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"
                          >
                            <ChevronsUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => mover(clave, -1)}
                            disabled={i === 0}
                            title="Una posición hacia la base"
                            className="p-0.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => mover(clave, 1)}
                            disabled={i === ordenStack.length - 1}
                            title="Una posición hacia arriba"
                            className="p-0.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => alExtremo(clave, "final")}
                            disabled={i === ordenStack.length - 1}
                            title="Bajar al final (hasta arriba del apilado)"
                            className="p-0.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"
                          >
                            <ChevronsDown className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => alternar(clave)}
                            title={off ? "Mostrar" : "Ocultar"}
                            className="p-0.5 text-slate-400 hover:text-slate-800"
                          >
                            {off ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="h-[560px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={filas} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="periodo"
                    tick={{ fontSize: 11, fill: "#475569" }}
                    tickFormatter={fmtPeriodo}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#475569" }}
                    tickFormatter={(v) => nf0.format(Number(v))}
                    width={80}
                    domain={[0, maxY || "auto"]}
                    allowDataOverflow
                  />
                  <Tooltip
                    labelFormatter={(l) => fmtPeriodoLargo(String(l))}
                    formatter={(v: number | string, name: string) => [
                      `${nf0.format(Number(v))} MW`,
                      etiqueta.get(name) ?? name,
                    ]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: "pointer" }}
                    onClick={(e) => alternar(String((e as { dataKey?: string }).dataKey ?? ""))}
                    formatter={(name: string) => (
                      <span
                        style={{
                          color: apagadas.has(name) ? "#cbd5e1" : "#475569",
                          textDecoration: apagadas.has(name) ? "line-through" : "none",
                        }}
                      >
                        {recorta(etiqueta.get(name) ?? name, 34)}
                      </span>
                    )}
                  />
                  {tipo === "apiladas"
                    ? // Bandas APILADAS: el techo del stack es la compra total de
                      // las contrapartes encendidas. Solo se dibujan las visibles,
                      // porque una banda oculta seguiría ocupando su lugar en el
                      // stack; encenderlas de nuevo se hace en el panel de orden.
                      apiladas.map((s) => (
                        <Area
                          key={s.participante}
                          type="monotone"
                          dataKey={s.participante}
                          name={s.participante}
                          stackId="mda"
                          stroke={colorPor.get(s.participante) ?? GRIS}
                          strokeWidth={1}
                          fill={colorPor.get(s.participante) ?? GRIS}
                          fillOpacity={0.55}
                          activeDot={{ r: 3 }}
                          isAnimationActive={false}
                        />
                      ))
                    : data.series.map((s) => (
                        <Line
                          key={s.participante}
                          type="monotone"
                          dataKey={s.participante}
                          name={s.participante}
                          stroke={colorPor.get(s.participante) ?? GRIS}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                          isAnimationActive={false}
                          hide={apagadas.has(s.participante)}
                        />
                      ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Acumulado del periodo: pastel + tabla */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-1">
                Acumulado del periodo por contraparte
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Las {PALETA.length} mayores; el resto se pliega en “Otros”. El
                porcentaje es sobre lo mostrado: apagar una contraparte en la
                leyenda de arriba también la saca de aquí.
              </p>
              <div className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pastel}
                      dataKey="total"
                      nameKey="nombre"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      innerRadius={54}
                      paddingAngle={1}
                      stroke="#ffffff"
                      strokeWidth={2}
                      isAnimationActive={false}
                    >
                      {pastel.map((p) => (
                        <Cell key={p.key} fill={p.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v: number | string, name: string) => [
                        `${nf0.format(Number(v))} MW (${nf1.format(pctPastel(Number(v)))} %)`,
                        name,
                      ]}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                      formatter={(name: string) => recorta(name, 42)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-100 text-sm font-semibold text-slate-700 flex items-center justify-between">
                <span>Acumulado por contraparte</span>
                <span className="text-xs font-normal text-slate-500">
                  clic para graficarla sola
                </span>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left sticky top-0">
                    <tr>
                      <th className="px-3 py-2 font-medium">Contraparte</th>
                      <th className="px-3 py-2 font-medium text-right">MW</th>
                      <th className="px-3 py-2 font-medium text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.totales.map((t) => (
                      <tr
                        key={t.participante}
                        onClick={() => {
                          const f = { ...form, seleccion: [t.participante] };
                          setForm(f);
                          buscar(f);
                        }}
                        className="border-t hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-3 py-1.5">
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-sm shrink-0"
                              style={{ background: colorPor.get(t.participante) ?? GRIS }}
                            />
                            <span className="font-mono text-xs">{t.participante}</span>
                            <span className="text-slate-600">{t.nombre}</span>
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {nf0.format(t.total)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                          {nf1.format(pctDe(t.total))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Un solo comprador (CFE Suministrador de Servicios Básicos) concentra
            la mayor parte del mercado, así que domina el pastel. Para comparar al
            resto entre sí, elige contrapartes en el filtro o haz clic en un
            renglón de la tabla.
          </p>
        </>
      )}

      {data && data.periodos.length === 0 && !cargando && (
        <div className="bg-white border rounded-lg p-6 text-center text-slate-500 text-sm">
          Sin compras publicadas en ese rango.
        </div>
      )}
    </div>
  );
}
